import type { Project, ProjectTypeKey } from '@/types/entities'
import type { Metric, Milestone, Risk } from '@/features/projects'

/**
 * The Client Project lifecycle, as the API serves it (backend `modules/lifecycle`). The rules
 * themselves — phase lengths, exit gates, the task template, health — are the server's; this file
 * only names things and does the arithmetic a form needs before there is anything to send.
 */

export const PHASES = ['pre_sales', 'pilot', 'implementation', 'hypercare', 'post_sales', 'additional_requirements', 'offboarding'] as const
export type Phase = (typeof PHASES)[number]

export const PHASE_LABEL: Record<Phase, string> = {
  pre_sales: 'Discovery',
  pilot: 'Pilot',
  implementation: 'Implementation',
  hypercare: 'Hypercare',
  post_sales: 'Post-Sales',
  additional_requirements: 'Additional Requirements',
  offboarding: 'Offboarding',
}
export const PHASE_NUMBER: Record<Phase, number> = { pre_sales: 1, pilot: 2, implementation: 3, hypercare: 4, post_sales: 5, additional_requirements: 6, offboarding: 7 }

/** Bar colours on the Gantt, one per phase. Full class names: Tailwind cannot see built ones. */
export const PHASE_BAR: Record<Phase, string> = {
  pre_sales: 'bg-brand-accent',
  pilot: 'bg-warning',
  implementation: 'bg-brand',
  hypercare: 'bg-info',
  post_sales: 'bg-success',
  additional_requirements: 'bg-brand-dark',
  offboarding: 'bg-text-muted',
}

/** Default lengths in days, for laying out the form's phase dates before anything is saved. */
export const PHASE_DAYS = { pre_sales: 14, pilot: 28, implementation: 28, hypercare: 14 } as const
export const DEFAULT_TARGET_DAYS = 84

export const TEAMS = ['am', 'ops', 'pe', 'client'] as const
export type OwnerTeam = (typeof TEAMS)[number]
export const TEAM_LABEL: Record<OwnerTeam, string> = { am: 'AM', ops: 'Ops & Support', pe: 'P&E', client: 'Client' }
export const TEAM_LONG: Record<OwnerTeam, string> = { am: 'Account Manager', ops: 'Operations & Support', pe: 'Product & Engineering', client: 'Client' }

export const PRODUCTS = [
  { key: 'ats_crm', label: 'ATS / CRM' },
  { key: 'ella', label: 'ELLA' },
  { key: 'tovi', label: 'TOVI' },
  { key: 'nova', label: 'NOVA' },
  { key: 'sera', label: 'SERA' },
  { key: 'custom_dashboards', label: 'Custom Dashboards' },
  { key: 'journeys', label: 'Journeys' },
  { key: 'one_link', label: 'One Link' },
  { key: 'assessments', label: 'Assessments' },
  { key: 'ai_train', label: 'AI Train' },
] as const
export type ProductKey = (typeof PRODUCTS)[number]['key']
export const productLabel = (key: string) => PRODUCTS.find((p) => p.key === key)?.label ?? key
export const PRODUCT_STATUSES = ['proposed', 'contracted', 'live'] as const
export type ProductStatus = (typeof PRODUCT_STATUSES)[number]
export const PRODUCT_STATUS_LABEL: Record<ProductStatus, string> = { proposed: 'Proposed', contracted: 'Contracted', live: 'Live' }

export const TASK_STATUSES = ['not_started', 'in_progress', 'blocked', 'done'] as const
export type TaskStatus = (typeof TASK_STATUSES)[number]
export const TASK_STATUS_LABEL: Record<TaskStatus, string> = { not_started: 'Not started', in_progress: 'In progress', blocked: 'Blocked', done: 'Done' }
export const TASK_KINDS = ['task', 'support', 'bug', 'feature_gap', 'flag'] as const
export type TaskKind = (typeof TASK_KINDS)[number]
export const TASK_KIND_LABEL: Record<TaskKind, string> = { task: 'Task', support: 'Support', bug: 'Bug', feature_gap: 'Feature gap', flag: 'Flag' }
export const PRIORITIES = ['low', 'medium', 'high', 'critical'] as const
export type Priority = (typeof PRIORITIES)[number]

export type Health = 'green' | 'yellow' | 'red'
export const HEALTH_LABEL: Record<Health, string> = { green: 'Green', yellow: 'Yellow', red: 'Red' }
export const HEALTH_DOT: Record<Health, string> = { green: 'bg-success', yellow: 'bg-warning', red: 'bg-danger' }
export const HEALTH_TEXT: Record<Health, string> = { green: 'text-success', yellow: 'text-warning', red: 'text-danger' }
export const HEALTH_SOFT: Record<Health, string> = { green: 'bg-success-soft text-success', yellow: 'bg-warning-soft text-warning', red: 'bg-danger-soft text-danger' }

export interface ProductRow { key: ProductKey; status: ProductStatus }
export interface ContactRow { id: string; name: string; role: string; email: string; decisionMaker: boolean; sponsor: boolean; left: boolean }
export interface ScopeRow { id: string; text: string; inScope: boolean; addTask: boolean }
export interface PhaseRow {
  id: string
  cycle: number
  phase: Phase
  startAt: string
  endAt: string | null
  status: 'upcoming' | 'active' | 'done' | 'skipped'
  gate: { metAt: string; by: string; checks: string[]; decision: 'go' | 'no_go' | null; note: string } | null
  extensionDays: number
}
export interface CycleRow { n: number; name: string; scope: string; skipPilot: boolean; openedAt: string; closedAt: string | null; projectId?: string | null }

export interface Lifecycle {
  projectId: string
  status: 'active' | 'offboarding' | 'closed'
  currentPhase: Phase
  currentCycle: number
  startAt: string
  targetPostSalesAt: string | null
  renewalAt: string | null
  lastContactAt: string | null
  problem: string | null
  requirements: string | null
  products: ProductRow[]
  contacts: ContactRow[]
  team: { ops: string[]; pe: string[] }
  scope: ScopeRow[]
  phases: PhaseRow[]
  cycles: CycleRow[]
  flags: { links: Record<string, { taskId: string; since: string; at: string }>; cleared: Record<string, { since: string; at: string; by: string }>; escalations: Array<{ at: string; by: string; note: string }> }
  changeLog: Array<{ at: string; by: string; what: string }>
  version: number
}

export interface RegistryTask {
  id: string
  projectId: string
  seq: number
  title: string
  phase: Phase
  cycle: number
  ownerTeam: OwnerTeam
  assigneeId: string | null
  startAt: string | null
  dueAt: string | null
  status: TaskStatus
  priority: Priority
  kind: TaskKind
  dependsOn: string | null
  riskId: string | null
  notes: string | null
  templateKey: string | null
  blockedAt: string | null
  doneAt: string | null
  version: number
}

export interface Flag {
  key: string
  signal: string
  level: 'yellow' | 'red'
  team: 'am' | 'ops' | 'pe'
  title: string
  detail: string
  since: string
  taskId: string | null
  linkedTaskId: string | null
  linkedTaskDone: boolean
}

export interface Dashboard {
  project: Project
  lifecycle: Lifecycle
  tasks: RegistryTask[]
  plan: { aim: string | null; strategy: string | null; scopeIn: string | null; scopeOut: string | null; metrics: Metric[]; milestones: Milestone[]; risks: Risk[]; version: number | null }
  health: { level: Health; flags: Flag[]; needsEscalation: boolean }
  gates: Record<Phase, string[]>
}

export interface LifecycleSummary {
  projectId: string
  status: Lifecycle['status']
  currentPhase: Phase
  currentCycle: number
  targetPostSalesAt: string | null
  products: ProductRow[]
  health: Health
  flagCount: number
}

/* ------------------------------------------------------------------ dates */

const DAY = 86_400_000
/** `YYYY-MM-DD` for a date input, read in the viewer's own zone. */
export const dayOf = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('en-CA') : '')
/** A date input's value as the start of that day in the viewer's zone. */
export const isoOfDay = (day: string) => new Date(`${day}T00:00:00`).toISOString()
export const addDaysToDay = (day: string, days: number) => {
  const d = new Date(`${day}T00:00:00`)
  d.setDate(d.getDate() + days)
  return d.toLocaleDateString('en-CA')
}
export const shortDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'
export const longDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'
export const daysUntil = (iso: string | null | undefined, now = Date.now()) => (iso ? Math.ceil((Date.parse(iso) - now) / DAY) : null)
export const ageInDays = (iso: string, now = Date.now()) => Math.max(0, Math.floor((now - Date.parse(iso)) / DAY))

/** Lay the first cycle out from a start day, for the form. Mirrors the server's `planCycle`. */
export function defaultPhaseDays(startDay: string, skipPilot: boolean): Array<{ phase: 'pre_sales' | 'pilot' | 'implementation' | 'hypercare'; start: string; end: string }> {
  const out: Array<{ phase: 'pre_sales' | 'pilot' | 'implementation' | 'hypercare'; start: string; end: string }> = []
  let cursor = startDay
  for (const phase of ['pre_sales', 'pilot', 'implementation', 'hypercare'] as const) {
    if (phase === 'pilot' && skipPilot) continue
    const end = addDaysToDay(cursor, PHASE_DAYS[phase])
    out.push({ phase, start: cursor, end })
    cursor = end
  }
  return out
}

export const isOverdue = (t: Pick<RegistryTask, 'status' | 'dueAt'>, now = Date.now()) => t.status !== 'done' && Boolean(t.dueAt) && Date.parse(t.dueAt!) < now

/** The phase the project is in, or its cycle number, as people say it. */
export function phaseLine(l: Pick<Lifecycle, 'status' | 'currentPhase' | 'currentCycle'>): string {
  if (l.status === 'closed') return 'Closed'
  const n = PHASE_NUMBER[l.currentPhase]
  return `${n}. ${PHASE_LABEL[l.currentPhase]}${l.currentCycle > 1 && l.currentPhase !== 'post_sales' ? ` · Cycle ${l.currentCycle}` : ''}`
}

/* ------------------------------------------------------------------ migration */

export interface RelatedProject { id: string; name: string; type: ProjectTypeKey; archived: boolean; ownerId: string | null; createdAt: string; cycle?: number | null }

export interface MigrationGroup {
  key: string
  clientId: string
  geographyId: string | null
  name: string
  existingId: string | null
  mostRecentId: string | null
  phase: Phase
  ownerId: string | null
  /** The existing project chosen to become the Client Project itself. */
  mainId: string | null
  candidates: Array<{ id: string; name: string; from: ProjectTypeKey }>
  /** In their order on the timeline (1 = first). */
  convert: Array<{ id: string; name: string; from: ProjectTypeKey; clientId: string; geographyId: string | null; clientName: string; order: number; startAt: string; endAt: string }>
  joined: string[]
}
export interface MigrationResult {
  dryRun: boolean
  groups: MigrationGroup[]
  done: Array<{ key: string; projectId: string }>
  failed?: Array<{ key: string; name: string; error: string }>
}
